import { useState } from 'react';

import {
    useGetContext,
    useGetCustomerState,
    useGetFormalOffer
} from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';

import messages from './FundingRequirementsView.messages';
import styles from './FundingRequirementsView.module.css';
import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { useTrackPageView } from '../../../tracking/pageView';
import { getDrawdownType } from '../../drawDown/getDrawdownType';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { getLoanDurationInMonths } from '../../myOffers/MyOffers.helpers';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { MoneyNoDecimals } from '../../ui/moneyNoDecimals';
import { Tooltip } from '../../ui/tooltip';
import FundingHeader from '../fundingHeader/FundingHeader';
import FundingHeaderLoading from '../fundingHeader/FundingHeaderLoading';
import { FundingProgressIndicator } from '../fundingSteps/FundingProgessIndicator/FundingProgressIndicator';
import FundingSteps from '../fundingSteps/FundingSteps';

const FundingRequirementsView = () => {
    useTrackPageView({ name: 'Funding Requirements' });

    const stateKey = useStateKey();
    const location = getCurrentLocation();
    const { data: customerState, isLoading: loadingCustomerState } =
        useGetCustomerState(stateKey);
    const { data } = useGetContext();

    const isAccessUser = data?.logged_in_user_type === 'access_user';

    let drawdownAmount: string | number | undefined = getQueryParameter(
        location,
        'amount'
    );
    const sweep = getQueryParameter(location, 'sweep');

    const offerId: string | undefined = getQueryParameter(location, 'offerId');

    const [hideBackButton, setHideBackButton] = useState(false);

    if (!drawdownAmount) {
        IwDataDogLogger.logError(
            'getQueryParameter drawdownAmount is undefined',
            {
                tags: {
                    reporter: 'funding_requirements_view'
                }
            }
        );
    }
    if (!offerId) {
        IwDataDogLogger.logError('getQueryParameter offerId is undefined', {
            tags: {
                reporter: 'funding_requirements_view'
            }
        });
    }

    const { formalOffer, loadingFormalOffer } = useGetFormalOffer(offerId);

    if (loadingFormalOffer || loadingCustomerState) {
        return (
            <MainSectionWrapper>
                <FundingHeaderLoading />
                <div className={styles.container}></div>
            </MainSectionWrapper>
        );
    }

    if (formalOffer && customerState?.application && drawdownAmount) {
        const isInterestOnlyExtension =
            getDrawdownType(formalOffer) === 'interest_only_extension';

        drawdownAmount = parseInt(drawdownAmount);

        tracking.ampli.drawdownStarted({
            amount: drawdownAmount,
            formalOfferId: offerId!
        });

        const loanDuration: number = getLoanDurationInMonths(
            formalOffer.duration!
        );

        const businessName =
            customerState.application.company!.registered_company_name;

        return (
            <>
                {getCountry() === 'UK' && !isAccessUser && (
                    <FundingProgressIndicator step={2} />
                )}

                <FundingHeader
                    header={
                        <FormattedMessage
                            {...getHeaderMessage(formalOffer, isAccessUser)}
                            values={{
                                drawdownAmount: (
                                    <span className={styles.headerBlue}>
                                        <MoneyNoDecimals
                                            value={drawdownAmount}
                                        />
                                    </span>
                                ),
                                loanDuration: (
                                    <span className={styles.headerBlue}>
                                        <FormattedMessage
                                            {...messages.duration}
                                            values={{
                                                loanDuration: loanDuration
                                            }}
                                        />
                                    </span>
                                ),
                                tooltip: (
                                    <span>
                                        <Tooltip
                                            className={styles.tooltip}
                                            message={
                                                <FormattedMessage
                                                    {...messages.tooltip}
                                                />
                                            }
                                            placement="auto"
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                    subHeader={
                        <FormattedMessage
                            {...getSubHeaderMessage(formalOffer, isAccessUser)}
                            values={{
                                businessName: <span>{businessName}</span>
                            }}
                        />
                    }
                    hideBackButton={hideBackButton}
                />

                <MainSectionWrapper>
                    <div className={styles.container}>
                        <FundingSteps
                            formalOffer={formalOffer}
                            drawdownAmount={drawdownAmount}
                            sweep={sweep}
                            isInterestOnlyExtension={isInterestOnlyExtension}
                            isAccessUser={isAccessUser}
                            setHideBackButton={setHideBackButton}
                        />
                    </div>
                </MainSectionWrapper>
            </>
        );
    }
    return <FallbackErrorModal />;
};

// eslint-disable-next-line import/no-default-export
export default FundingRequirementsView;

const getHeaderMessage = (formalOffer: TFormalOffer, isAccessUser: boolean) => {
    if (isAccessUser) return messages.accessUserHeader;

    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'interest_only_extension':
            return messages.interestOnlyHeader;
        case 'topup':
        case 'repayment_reduction':
        default:
            return messages.header;
    }
};

const getSubHeaderMessage = (
    formalOffer: TFormalOffer,
    isAccessUser: boolean
) => {
    if (isAccessUser) return messages.accessUserSubHeader;

    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'repayment_reduction':
            return messages.repaymentReductionSubHeader;
        case 'interest_only_extension':
            return messages.interestOnlySubHeader;
        case 'topup':
        default:
            return messages.subHeader;
    }
};
